import { useCallback, useContext, useState } from "react";
import { buildFullLink, getPath } from "common/routing";
import { getAppPrefix, getClientAppPrefix } from "common/utils/url";
import { AuthContext } from "../Authorization/AuthContext";
import { AuthContextType, CurrentUser } from "../Authorization/types";
import { AccountLogoOrFallbackIcon } from "../Common/AccountLogo";
import { Body } from "../Common/Typography";
import {
  AccountSwitcherAccountsSection,
  AccountSwitcherHelperLinksList,
  AccountSwitcherSection,
  AccountSwitcherWrapper,
  StyledAccountLinkListItem,
  StyledAccountLinksList,
} from "./__styles__/AccountSwitcher";

type ActiveAppAccount = NonNullable<AuthContextType["account"]>;
type UserAccount = NonNullable<AuthContextType["user"]>["accounts"][number];

const STATIC_LINKS = [
  {
    key: "help-center",
    href: "https://forerunner.helpscoutdocs.com",
    label: "Help center",
  },
  {
    key: "release-notes",
    href: "https://www.withforerunner.com/resources/product-updates",
    label: "Release notes",
  },
  {
    key: "get-ios-android-apps",
    href: "https://www.withforerunner.com/feature/mobile-workflows",
    label: "Get iOS & Android apps",
  },
];

const buildLastLinkMetadata = (
  isAdminUser: boolean,
  isImpersonating: boolean,
) => {
  const href =
    isImpersonating ? getPath("logout")
    : isAdminUser ?
      buildFullLink("adminAccounts", {
        prefix: getAppPrefix(),
        params: {},
      })
    : getPath("logout");

  const label =
    isImpersonating ? "Stop impersonating"
    : isAdminUser ? "Accounts"
    : "Log out";

  return { href, label };
};

const AccountListItemLink = ({
  account,
  disabled,
  onBlur,
  onFocus,
  selected,
  userEmail,
}: {
  account: UserAccount;
  disabled: boolean;
  onBlur: () => void;
  onFocus: (account: UserAccount) => void;
  selected: boolean;
  userEmail: string;
}) => {
  const href = buildFullLink("provideCredentials", {
    prefix: getClientAppPrefix(account.subdomain),
    params: {},
    queryParams: {
      email: userEmail,
    },
  });

  return (
    <StyledAccountLinkListItem
      disabled={disabled}
      onBlur={onBlur}
      onFocus={() => onFocus(account)}
      selected={selected}
    >
      <a href={href}>
        <AccountLogoOrFallbackIcon account={account} />
        <Body as="span" size="default" type="emphasis">
          {account.name}
        </Body>
      </a>
    </StyledAccountLinkListItem>
  );
};

const AccountList = ({
  activeAccount,
  user,
}: {
  activeAccount: ActiveAppAccount;
  user: CurrentUser;
}) => {
  const [selectedAccount, setSelectedAccount] = useState<Maybe<UserAccount>>(
    () => {
      return (
        user.accounts.find(account => account.id === activeAccount?.id) ?? null
      );
    },
  );

  const handleOnFocus = useCallback(
    (account: UserAccount) => {
      setSelectedAccount(account);
    },
    [setSelectedAccount],
  );

  const handleOnBlur = useCallback(() => {
    setSelectedAccount(activeAccount);
  }, [activeAccount, setSelectedAccount]);

  const disabled = user.accounts.length === 1;

  return (
    <AccountSwitcherAccountsSection>
      <StyledAccountLinksList>
        {user.accounts.map(account => (
          <AccountListItemLink
            key={account.id}
            account={account}
            userEmail={user.email}
            disabled={disabled}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            selected={!!selectedAccount && account.id === selectedAccount.id}
          />
        ))}
      </StyledAccountLinksList>
    </AccountSwitcherAccountsSection>
  );
};

const AccountSwitcherUtilLink = ({
  href,
  label,
  target,
}: {
  href: string;
  label: string;
  target?: string;
}) => (
  <li>
    <a href={href} target={target}>
      <Body as="span" size="default" type="regular">
        {label}
      </Body>
    </a>
  </li>
);

export const AccountSwitcher = () => {
  const { account: activeAccount, admin, user } = useContext(AuthContext);
  const isAdminUser = Boolean(admin);
  const isImpersonating = Boolean(user?.isBeingImpersonated);
  const hasAccounts = user?.accounts && user.accounts.length > 0;
  const shouldRenderAccountList = activeAccount && hasAccounts;

  return (
    <AccountSwitcherWrapper>
      {shouldRenderAccountList && (
        <AccountList activeAccount={activeAccount} user={user} />
      )}
      <AccountSwitcherSection>
        <AccountSwitcherHelperLinksList>
          {STATIC_LINKS.map(({ key, href, label }) => (
            <AccountSwitcherUtilLink
              key={key}
              href={href}
              label={label}
              target="_blank"
            />
          ))}
          <AccountSwitcherUtilLink
            {...buildLastLinkMetadata(isAdminUser, isImpersonating)}
          />
        </AccountSwitcherHelperLinksList>
      </AccountSwitcherSection>
    </AccountSwitcherWrapper>
  );
};
